import UnzipRequest from "./UnzipRequest";
import ZipManager from "./ZipManager";

/**
* ZipLoadQueue.js
*
* Adds zip loading functionality to createjs' Loaders.
* See also 'ranj.UnzipRequest.js'
*
* Overrides some createjs.LoadQueue methods to fix the fact that progress reporting becomes skewed due to zip loading.
* We solve this by calculating overall progress based on weight per item:
* - For the first requested item in a bundle: give it an initial weight of 1
* - For subsequent requested items in a bundle: use a weight of 0.1, and add 1 to the weight of the first item.
* - For items not in a bundle: use a weight of 1
* In this, it is assumed that the zipped size of most files is more or less the same as the non-zipped size, 
* which is probable for images and sounds.
* This could be further finetuned by assuming specific weights per file type (e.g. a json is in most cases much smaller than an image)
* or even using different weights per type for unzipped and zipped files (e.g. a text file will be much more compressed than an image)
*
* 2020-01-29
* @author reinier@ranj.nl
**/
class ZipLoadQueue extends createjs.LoadQueue {
    constructor(preferXHR, basePath, crossOrigin) {
        super(preferXHR, basePath, crossOrigin);
        this._bundleData = {};
        this._weightLoaded = 0;
        this._weightTotal = 0;
    };

    _createLoadItem(value, path, basePath) {
        const item = super._createLoadItem(value, path, basePath);
        //Add weight to item for progress reporting
        const bundleId = item.bundle;
        item.weight = 1;
        if (bundleId) {
            const bundleData = this._bundleData[bundleId];
            if (bundleData == null) {
                this._bundleData[bundleId] = { loaded: false, firstItem: item };
            } else {
                //If bundle already loaded, dont add weight for bundle
                if (!bundleData.loaded) {
                    bundleData.firstItem.weight++;
                    this._weightTotal++;
                }
                //Subsequent items from bundle should be very fast:
                item.weight = .1;
            }
        }
        this._weightTotal += item.weight;
        return item;
    };

    _sendComplete() {
        this._weightLoaded = 0;
        this._weightTotal = 0;
        //Remove references to loaded items, 
        //but remember bundle is already loaded in case new items from same bundle are requested later
        for (let bundleId in this._bundleData) {
            this._bundleData[bundleId].loaded = true;
            this._bundleData[bundleId].firstItem = null;
        }
        super._sendComplete();
    }

    _processFinishedLoad(item, loader) {
        this._weightLoaded += item.weight;
        super._processFinishedLoad(item, loader);
    }

    _updateProgress() {
        let loaded = this._weightLoaded;
        for (let i = 0, l = this._currentLoads.length; i < l; i++) {
            const loader = this._currentLoads[i];
            loaded += loader.progress * loader.getItem().weight;
        }
        const progress = loaded / this._weightTotal;
        if (this._lastProgress != progress) {
            this._sendProgress(progress);
            this._lastProgress = progress;
        }
    };

    initializeZipLoading(ziploadingFolder) {
        if (this._zipManager == null) {
            this._zipManager = new ZipManager(ziploadingFolder);
        }
        const zipManager = this._zipManager;
        //Overwrite _createRequest on Loader prototypes:
        ZipLoadQueue.LOADERCLASSES.forEach(function (loaderClass) {
            //AbstractLoader._createRequest is called from within AbstractLoader.load
            const loaderPrototype = loaderClass.prototype;
            loaderPrototype._createRequestOriginal = loaderPrototype._createRequest;
            loaderPrototype._createRequest = function () {
                if (this._item.bundle != null) {
                    const params = getTypeParams(this._item.type);
                    this._request = new UnzipRequest(this._item, zipManager, params.contentType, params.resultFormatter, this.getTag());
                } else {
                    this._createRequestOriginal();
                }
            }
        });
    }

    clearAllZipData() {
        if (this._zipManager != null) {
            this._zipManager.removeAll();
            this._bundleData = {};
        }
    }

    get zipManager() {
        return this._zipManager;
    }

    get bundleData() {
        return this._bundleData;
    }
}

const getTypeParams = function(itemType) {
    switch (itemType) {
        case createjs.Types.IMAGE:
            return ZipLoadQueue.IMAGE_PARAMS;
        case createjs.Types.SOUND:
            if (createjs.Sound.activePlugin instanceof createjs.HTMLAudioPlugin) {
                return ZipLoadQueue.MEDIA_PARAMS_TAGLOADING;
            }
            return ZipLoadQueue.SOUND_PARAMS;
        case createjs.Types.VIDEO:
            return ZipLoadQueue.MEDIA_PARAMS_TAGLOADING;
        default:
            return ZipLoadQueue.DEFAULT_PARAMS;
    }
}
ZipLoadQueue.DEFAULT_PARAMS = {
    contentType: "text"
};

ZipLoadQueue.IMAGE_PARAMS = {
    contentType: "uint8array",
    resultFormatter: function (data) {
        return new Blob([data]);
    }
};

ZipLoadQueue.SOUND_PARAMS = {
    contentType: "arraybuffer"
};

ZipLoadQueue.MEDIA_PARAMS_TAGLOADING = {
    contentType: "arraybuffer",
    resultFormatter: function (data, item, tag) {
        const blob = new Blob([data]);
        tag.src = URL.createObjectURL(blob);
        return blob;
    }
};

ZipLoadQueue.LOADERCLASSES = [
    createjs.ImageLoader,
    createjs.WebAudioLoader,
    createjs.SoundLoader, //used when WebAudio not supported (i.e. in IE)
    createjs.VideoLoader,
    createjs.JSONLoader,
    createjs.XMLLoader,
    createjs.TextLoader
];

export default ZipLoadQueue;